/**
 * ConfigWizard — Guided multi-step wizard for first-time config creation
 *
 * 6 steps: Process → Steps → Triggers → Tracking Values → Conditions/Custom → Review
 * Each step introduces one concept with help panels and validation.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ProcessForm from './ProcessForm';
import StepForm from './StepForm';
import ValidationSummary from './ValidationSummary';
// labelMapping used by StepForm/ProcessForm components
import {
  EMPTY_STEP, EMPTY_IDENTIFIER, EMPTY_CONDITION, EMPTY_CUSTOM_FIELD,
} from './utils/configTransforms';
import useConfigValidation from './hooks/useConfigValidation';

const WIZARD_STEPS = [
  { number: 1, label: 'Name Process' },
  { number: 2, label: 'Define Steps' },
  { number: 3, label: 'Triggers' },
  { number: 4, label: 'Tracking Values' },
  { number: 5, label: 'Conditions' },
  { number: 6, label: 'Review' },
];

export default function ConfigWizard({ processes, setProcesses, onSaveDraft, onSavePublish, saving }) {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [visitedSteps, setVisitedSteps] = useState(new Set([1]));
  const { errors, validateWizardStep, validate, clearErrors } = useConfigValidation();
  const [showValidation, setShowValidation] = useState(false);

  const process = processes[0]; // wizard works on a single process

  // --- Process/step update helpers ---
  function updateProcess(pi, field, value) {
    setProcesses(prev => prev.map((p, i) => i === pi ? { ...p, [field]: value } : p));
  }
  function updateStep(pi, si, field, value) {
    setProcesses(prev => prev.map((p, i) =>
      i === pi ? { ...p, steps: p.steps.map((s, j) => j === si ? { ...s, [field]: value } : s) } : p));
  }
  function addStep(pi) {
    setProcesses(prev => prev.map((p, i) =>
      i === pi ? { ...p, steps: [...p.steps, EMPTY_STEP()] } : p));
  }
  function removeStep(pi, si) {
    setProcesses(prev => prev.map((p, i) =>
      i === pi ? { ...p, steps: p.steps.filter((_, j) => j !== si) } : p));
  }
  function addIdentifier(pi, si) {
    setProcesses(prev => prev.map((p, i) =>
      i === pi ? { ...p, steps: p.steps.map((s, j) =>
        j === si ? { ...s, identifiers: [...s.identifiers, EMPTY_IDENTIFIER()] } : s) } : p));
  }
  function removeIdentifier(pi, si, ii) {
    setProcesses(prev => prev.map((p, i) =>
      i === pi ? { ...p, steps: p.steps.map((s, j) =>
        j === si ? { ...s, identifiers: s.identifiers.filter((_, k) => k !== ii) } : s) } : p));
  }
  function updateIdentifier(pi, si, ii, field, value) {
    setProcesses(prev => prev.map((p, i) =>
      i === pi ? { ...p, steps: p.steps.map((s, j) =>
        j === si ? { ...s, identifiers: s.identifiers.map((id, k) =>
          k === ii ? { ...id, [field]: value } : id) } : s) } : p));
  }
  function addCondition(pi, si) {
    setProcesses(prev => prev.map((p, i) =>
      i === pi ? { ...p, steps: p.steps.map((s, j) =>
        j === si ? { ...s, conditions: [...s.conditions, EMPTY_CONDITION()] } : s) } : p));
  }
  function removeCondition(pi, si, ci) {
    setProcesses(prev => prev.map((p, i) =>
      i === pi ? { ...p, steps: p.steps.map((s, j) =>
        j === si ? { ...s, conditions: s.conditions.filter((_, k) => k !== ci) } : s) } : p));
  }
  function updateCondition(pi, si, ci, field, value) {
    setProcesses(prev => prev.map((p, i) =>
      i === pi ? { ...p, steps: p.steps.map((s, j) =>
        j === si ? { ...s, conditions: s.conditions.map((c, k) =>
          k === ci ? { ...c, [field]: value } : c) } : s) } : p));
  }
  function addCustomField(pi, si) {
    setProcesses(prev => prev.map((p, i) =>
      i === pi ? { ...p, steps: p.steps.map((s, j) =>
        j === si ? { ...s, custom_fields: [...s.custom_fields, EMPTY_CUSTOM_FIELD()] } : s) } : p));
  }
  function removeCustomField(pi, si, fi) {
    setProcesses(prev => prev.map((p, i) =>
      i === pi ? { ...p, steps: p.steps.map((s, j) =>
        j === si ? { ...s, custom_fields: s.custom_fields.filter((_, k) => k !== fi) } : s) } : p));
  }
  function updateCustomField(pi, si, fi, field, value) {
    setProcesses(prev => prev.map((p, i) =>
      i === pi ? { ...p, steps: p.steps.map((s, j) =>
        j === si ? { ...s, custom_fields: s.custom_fields.map((cf, k) =>
          k === fi ? { ...cf, [field]: value } : cf) } : s) } : p));
  }

  // --- Navigation ---
  function goNext() {
    clearErrors();
    // Validate current step (except step 5 which is optional)
    if (currentStep !== 5) {
      const result = validateWizardStep(currentStep, processes);
      if (!result.valid) return;
    }
    const next = Math.min(currentStep + 1, 6);
    setCurrentStep(next);
    setVisitedSteps(prev => new Set([...prev, next]));
  }

  function goBack() {
    clearErrors();
    setCurrentStep(Math.max(currentStep - 1, 1));
  }

  function goToStep(step) {
    if (visitedSteps.has(step)) {
      clearErrors();
      setCurrentStep(step);
    }
  }

  async function handlePublish() {
    const result = validate(processes);
    if (!result.valid) {
      setShowValidation(true);
      return;
    }
    const success = await onSavePublish();
    if (success) {
      setTimeout(() => navigate('/config/history'), 1800);
    }
  }

  async function handleSaveDraft() {
    const success = await onSaveDraft();
    if (success) {
      setTimeout(() => navigate('/config/history'), 1800);
    }
  }

  return (
    <div className="page">
      {/* Progress bar */}
      <div className="config-wizard-progress">
        {WIZARD_STEPS.map(ws => (
          <div
            key={ws.number}
            className={`config-wizard-step ${ws.number === currentStep ? 'active' : ''} ${visitedSteps.has(ws.number) ? 'visited' : ''}`}
            onClick={() => goToStep(ws.number)}
            role="button"
            tabIndex={0}
          >
            <div className="config-wizard-step-number">{ws.number}</div>
            <div className="config-wizard-step-label">{ws.label}</div>
          </div>
        ))}
      </div>

      {/* Validation errors */}
      {errors.length > 0 && (
        <ValidationSummary errors={errors} onDismiss={clearErrors} />
      )}

      {/* Step content */}
      <div className="config-wizard-content">
        {currentStep === 1 && (
          <div>
            <h2 style={{ marginBottom: '4px' }}>Name Your Process</h2>
            <p className="page-subtitle" style={{ marginBottom: '24px' }}>
              A process is a workflow you want to track — like "Invoice Approval" or "Employee Onboarding".
            </p>
            <ProcessForm
              process={process}
              processIndex={0}
              onUpdate={updateProcess}
              showHelp={true}
              showEnabled={false}
            />
          </div>
        )}

        {currentStep === 2 && (
          <div>
            <h2 style={{ marginBottom: '4px' }}>Define Your Steps</h2>
            <p className="page-subtitle" style={{ marginBottom: '24px' }}>
              Steps are the individual actions in your workflow. Each step happens on a specific web page.
            </p>
            {process.steps.map((step, si) => (
              <div key={si} className="config-wizard-step-card">
                <div className="flex items-center justify-between" style={{ marginBottom: '12px' }}>
                  <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)' }}>
                    Step {si + 1}{step.label ? ` — ${step.label}` : ''}
                  </span>
                  {process.steps.length > 1 && (
                    <button className="btn btn-ghost btn-sm text-danger" onClick={() => removeStep(0, si)}>
                      Remove
                    </button>
                  )}
                </div>
                <StepForm
                  step={step} processIndex={0} stepIndex={si}
                  onUpdateStep={(field, value) => updateStep(0, si, field, value)}
                  onUpdateIdentifier={() => {}} onAddIdentifier={() => {}}
                  onRemoveIdentifier={() => {}} onUpdateCondition={() => {}}
                  onAddCondition={() => {}} onRemoveCondition={() => {}}
                  onUpdateCustomField={() => {}} onAddCustomField={() => {}}
                  onRemoveCustomField={() => {}}
                  mode="wizard" activeSection="basics" showHelp={si === 0}
                />
              </div>
            ))}
            <button className="btn btn-secondary btn-sm" onClick={() => addStep(0)}>
              + Add step
            </button>
          </div>
        )}

        {currentStep === 3 && (
          <div>
            <h2 style={{ marginBottom: '4px' }}>Set Up Triggers</h2>
            <p className="page-subtitle" style={{ marginBottom: '24px' }}>
              Tell us which button or element to watch on each page. When a user clicks it, the step is captured.
            </p>
            {process.steps.map((step, si) => (
              <div key={si} className="config-wizard-step-card">
                <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', marginBottom: '12px', display: 'block' }}>
                  {step.label || `Step ${si + 1}`}
                </span>
                <StepForm
                  step={step} processIndex={0} stepIndex={si}
                  onUpdateStep={(field, value) => updateStep(0, si, field, value)}
                  onUpdateIdentifier={() => {}} onAddIdentifier={() => {}}
                  onRemoveIdentifier={() => {}} onUpdateCondition={() => {}}
                  onAddCondition={() => {}} onRemoveCondition={() => {}}
                  onUpdateCustomField={() => {}} onAddCustomField={() => {}}
                  onRemoveCustomField={() => {}}
                  mode="wizard" activeSection="trigger" showHelp={si === 0}
                />
              </div>
            ))}
          </div>
        )}

        {currentStep === 4 && (
          <div>
            <h2 style={{ marginBottom: '4px' }}>Set Up Tracking Values</h2>
            <p className="page-subtitle" style={{ marginBottom: '24px' }}>
              For each step, tell us which value on the page identifies what's being tracked — like an invoice number or order ID.
            </p>
            <div className="config-wizard-info-card" style={{ marginBottom: '20px' }}>
              <strong>Config</strong> = define what to extract &nbsp;→&nbsp; <strong>Reports</strong> = define how steps flow together
            </div>
            {process.steps.map((step, si) => (
              <div key={si} className="config-wizard-step-card">
                <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', marginBottom: '12px', display: 'block' }}>
                  {step.label || `Step ${si + 1}`}
                </span>
                <StepForm
                  step={step} processIndex={0} stepIndex={si}
                  onUpdateStep={(field, value) => updateStep(0, si, field, value)}
                  onUpdateIdentifier={(ii, field, value) => updateIdentifier(0, si, ii, field, value)}
                  onAddIdentifier={() => addIdentifier(0, si)}
                  onRemoveIdentifier={(ii) => removeIdentifier(0, si, ii)}
                  onUpdateCondition={() => {}} onAddCondition={() => {}} onRemoveCondition={() => {}}
                  onUpdateCustomField={() => {}} onAddCustomField={() => {}} onRemoveCustomField={() => {}}
                  mode="wizard" activeSection="tracking" showHelp={si === 0}
                />
              </div>
            ))}
          </div>
        )}

        {currentStep === 5 && (
          <div>
            <h2 style={{ marginBottom: '4px' }}>Conditions & Custom Fields</h2>
            <p className="page-subtitle" style={{ marginBottom: '8px' }}>
              These are optional. Skip this if you just want to track every click.
            </p>
            <p className="form-hint" style={{ marginBottom: '24px' }}>
              Conditions control when a step is captured. Custom fields extract extra data from the page.
            </p>
            {process.steps.map((step, si) => (
              <div key={si} className="config-wizard-step-card">
                <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', marginBottom: '12px', display: 'block' }}>
                  {step.label || `Step ${si + 1}`}
                </span>
                <StepForm
                  step={step} processIndex={0} stepIndex={si}
                  onUpdateStep={(field, value) => updateStep(0, si, field, value)}
                  onUpdateIdentifier={() => {}} onAddIdentifier={() => {}} onRemoveIdentifier={() => {}}
                  onUpdateCondition={(ci, field, value) => updateCondition(0, si, ci, field, value)}
                  onAddCondition={() => addCondition(0, si)}
                  onRemoveCondition={(ci) => removeCondition(0, si, ci)}
                  onUpdateCustomField={(fi, field, value) => updateCustomField(0, si, fi, field, value)}
                  onAddCustomField={() => addCustomField(0, si)}
                  onRemoveCustomField={(fi) => removeCustomField(0, si, fi)}
                  mode="wizard" activeSection="conditions" showHelp={si === 0}
                />
              </div>
            ))}
          </div>
        )}

        {currentStep === 6 && (
          <div>
            <h2 style={{ marginBottom: '4px' }}>Review & Publish</h2>
            <p className="page-subtitle" style={{ marginBottom: '24px' }}>
              Review your configuration before saving. You can go back to any step to make changes.
            </p>
            {showValidation && errors.length > 0 && (
              <ValidationSummary errors={errors} onNavigate={(pi, si, field) => {
                // Navigate to the relevant wizard step
                const stepMap = { process: 1, basics: 2, trigger: 3, tracking: 4, conditions: 5, customFields: 5 };
                const target = stepMap[field] || 1;
                goToStep(target);
              }} onDismiss={() => setShowValidation(false)} />
            )}

            {/* Read-only summary */}
            <div className="card" style={{ marginBottom: '20px' }}>
              <div className="card-header">
                <span className="card-title">{process.process_name || 'Untitled Process'}</span>
                <span className="font-mono text-sm text-secondary">{process.process_id}</span>
              </div>
              <div className="card-body" style={{ padding: 0 }}>
                {process.steps.map((step, si) => (
                  <div key={si} style={{
                    padding: '16px 24px',
                    borderBottom: si < process.steps.length - 1 ? '1px solid var(--border)' : 'none'
                  }}>
                    <div style={{ fontWeight: '600', fontSize: '14px', marginBottom: '8px' }}>
                      {step.label || `Step ${si + 1}`}
                      <span className="font-mono text-sm text-secondary" style={{ marginLeft: '8px' }}>{step.step_id}</span>
                    </div>
                    <div className="form-row" style={{ marginBottom: '8px' }}>
                      <div>
                        <span className="text-secondary" style={{ fontSize: '11px', textTransform: 'uppercase' }}>App:</span>{' '}
                        <span style={{ fontSize: '13px' }}>{step.application_name || '—'}</span>
                      </div>
                      <div>
                        <span className="text-secondary" style={{ fontSize: '11px', textTransform: 'uppercase' }}>URL:</span>{' '}
                        <span className="mono" style={{ fontSize: '13px' }}>{step.url_pattern || '—'}</span>
                      </div>
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                      <span>Trigger: <code>{step.trigger_selector || '—'}</code></span>
                      {step.identifiers.length > 0 && (
                        <span style={{ marginLeft: '16px' }}>
                          Tracking: {step.identifiers.map(id => id.label).filter(Boolean).join(', ') || '—'}
                        </span>
                      )}
                      {step.conditions.length > 0 && (
                        <span style={{ marginLeft: '16px' }}>
                          {step.conditions.length} condition{step.conditions.length !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Navigation buttons */}
      <div className="config-wizard-nav">
        <div>
          {currentStep > 1 && (
            <button className="btn btn-ghost" onClick={goBack}>← Back</button>
          )}
        </div>
        <div className="flex gap-8">
          {currentStep < 6 ? (
            <>
              {currentStep === 5 && (
                <button className="btn btn-ghost" onClick={goNext}>
                  Skip →
                </button>
              )}
              <button className="btn btn-primary" onClick={goNext}>
                Next →
              </button>
            </>
          ) : (
            <>
              <button className="btn btn-secondary" onClick={handleSaveDraft} disabled={saving}>
                Save as Draft
              </button>
              <button className="btn btn-primary" onClick={handlePublish} disabled={saving}>
                {saving ? 'Saving…' : 'Save & Publish'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
